import { AppApiGateway } from '@repo/api/web/AppApiGateway'
import type {
  CartProductListItemDto,
  CdekCitiesByLocationRequestQuery,
  CdekEstimatedIntervalsRequestQuery,
  CdekOfficesRequestQuery,
  CdekSuggestCitiesRequestQuery,
  OrderPayRequestV1,
  OrdersListRequestQuery,
  PochtaSearchOpsRequestQuery,
  UpdateOrder,
} from '@repo/api/web/models'
import { inject, injectable } from 'inversify'

import { UserService } from '#User/services'

@injectable()
export class OrderService {
  protected appConfig = useAppConfig()

  constructor(
    @inject(AppApiGateway) readonly api: AppApiGateway,
    @inject(UserService) readonly userService: UserService,
  ) {}

  getList(query: OrdersListRequestQuery) {
    return this.api.ordersList(this.appConfig.host, query)
  }

  getById(orderId: number) {
    return this.api.ordersGet(orderId, this.appConfig.host)
  }

  /** Публичные настройки заказов (в т.ч. каталог deliveryMethods). */
  getOrdersConfig() {
    return this.api.ordersOrdersConfig(this.appConfig.host)
  }

  create(model: UpdateOrder) {
    return this.api.ordersCreate(this.appConfig.host, model)
  }

  resolve(
    model: Pick<UpdateOrder, 'delivery' | 'products' | 'promoCode' | 'pointsToSpend'>,
  ) {
    return this.api.ordersResolve(this.appConfig.host, model as UpdateOrder)
  }

  pay(orderId: number, model: OrderPayRequestV1) {
    return this.api.ordersPayV1(orderId, this.appConfig.host, model)
  }

  cancel(orderId: number) {
    return this.api.ordersCancel(orderId, this.appConfig.host)
  }

  suggestCities(query: CdekSuggestCitiesRequestQuery) {
    return this.api.cdekSuggestCities(this.appConfig.host, query)
  }

  getCitiesByLocation(query: CdekCitiesByLocationRequestQuery) {
    return this.api.cdekCitiesByLocation(this.appConfig.host, query)
  }

  getOffices(query: CdekOfficesRequestQuery) {
    return this.api.cdekOffices(this.appConfig.host, query)
  }

  cdekEstimatedIntervals(query: CdekEstimatedIntervalsRequestQuery) {
    return this.api.cdekEstimatedIntervals(this.appConfig.host, query)
  }

  pochtaSearchOps(query: PochtaSearchOpsRequestQuery) {
    return this.api.pochtaSearchOps(this.appConfig.host, query)
  }

  /**
   * Подсчёт суммы товаров в заказе (без стоимости доставки)
   */
  calculateItemsCost(items: CartProductListItemDto[]): number {
    return items.reduce((acc, item) => {
      const price = item.wellKnownAttributes?.Price || 0
      return acc + price * (item.amount || 1)
    }, 0)
  }

  calculateTotalCost(itemsCost: number, deliveryCost: number): number {
    return itemsCost + deliveryCost
  }

  /**
   * @returns Возвращает частично заполненный UpdateOrder из профиля пользователя
   */
  prefillFromUser(): Partial<UpdateOrder> {
    const user = this.userService.getCurrentUser()
    if (!user) return {}

    return {
      fio: user.profile?.fullName || undefined,
      phone: user.phone || undefined,
      email: user.email || undefined,
    }
  }
}
